<template></template>
<script>
import axios from "axios";
import { constants } from "@/constants";
import Palyance from "@/services/palyance";
import Redirect from "@/services/redirect";
import Curiosity from "@/services/curiosity";
import { useOrderHistoryStore } from "@/store/orderHistory";
import AlreadyOrderedProducts from "@/services/alreadyOrderedProducts";
import { useDraftStore } from "@/store/draft";
import Navigation from "@/services/navigation";
import { stampConfirmationOfferWindow } from "@/services/confirmationOfferWindow";
import { fetchExtrasellWithStepAdvance } from "@/services/extrasellFunnel";
import { buildExtrasellPayload } from "@/services/extrasellPayload";
import {
  isVanguardOnlyExtrasellSession,
  selectExtrasellHistoryForPayload,
} from "@/services/confirmationPreview";

// Order Page UX: prefetch /extrasell to remove the 3-4s delay on confirmation page.
const EXTRASELL_PREFETCH_TIMEOUT_MS = 2000;
const EXTRASELL_CACHE_TTL_MS = 5 * 60 * 1000;
const EXTRASELL_CACHE_PREFIX = "extrasell_";

export default {
  name: "WaitingPayment",
  mounted() {
    this.main();
  },
  computed: {
    billingEmail() {
      return (
        useOrderHistoryStore().lastOrderHistoryItem?.order?.billingEmail || ""
      );
    },
  },
  methods: {
    main() {
      // FORGE-308: guard — ce composant ne doit s'exécuter QUE sur la waiting
      // page configurée par globalConfig. S'il est monté sur /error ou une
      // autre page (template généré incorrect), il ne doit pas lancer le
      // polling pour éviter la boucle infinie : error → poll → FAIL →
      // redirectToError → reload → ...
      // On compare au chemin canonique (`globalConfig.waitingPage`) plutôt
      // qu'à un suffixe hardcodé `/waiting`, qui peut diverger si la route
      // change (ex: `/orderpage1/waiting`).
      const pathname = window.location.pathname.replace(/\/+$/, "");
      const waitingPage = (window.globalConfig?.waitingPage || "").replace(
        /\/+$/,
        "",
      );
      const isWaitingPage = waitingPage.length > 0 && pathname === waitingPage;
      if (!isWaitingPage) {
        console.warn(
          "[WaitingPayment] Composant monté hors de la waiting page — abort.",
          { pathname, expected: waitingPage },
        );
        return;
      }

      const paramPlusData = Redirect.getParamPlusData();
      Redirect.checkAndGetQueryParams(paramPlusData);
      const queryParams = Object.fromEntries(Navigation.getQueryParams());

      if (!paramPlusData || !paramPlusData.orderId) {
        Redirect.redirectToError(paramPlusData);
        return;
      }

      const processedKey = `order_confirmed_${paramPlusData.orderId}`;
      try {
        if (sessionStorage.getItem(processedKey)) {
          Redirect.redirectToConfirmation(paramPlusData);
          return;
        }
      } catch (_) {
        /* storage unavailable (privacy mode, etc.) */
      }

      if (
        (paramPlusData.action === "recapture" &&
          queryParams.authorizationId === undefined) ||
        paramPlusData.paymentType === "sepa"
      ) {
        this.runPostPaymentPipeline(paramPlusData);
        return;
      }

      // Read query params to find authorizationId to get Ogone status
      if (!queryParams.authorizationId && !paramPlusData.orderId) {
        console.error(
          `No authorizationId found, aborting : paramPlusData: ${JSON.stringify(paramPlusData)}, queryParams: ${JSON.stringify(queryParams)}, orderId: ${paramPlusData.orderId}`,
        );
        Redirect.redirectToError(paramPlusData);

        return;
      }

      const startedAt = new Date().getTime();
      const byOrderId = paramPlusData.authorizationType === "capture_now";
      const intervalId = setInterval(() => {
        this.getOgoneStatus(
          queryParams.authorizationId,
          paramPlusData.orderId,
          byOrderId,
          (data) => {
            if (!data) {
              return;
            }

            if (
              data.status === "NOT_FOUND" ||
              data.status === "FAIL" ||
              data.status === "UNK"
            ) {
              clearInterval(intervalId);
              Redirect.redirectToError(paramPlusData, {
                reason: "payment_failed",
                ogoneStatus: data.status,
              });

              return;
            }

            const now = new Date().getTime();
            if (now - startedAt > constants.AUTHORIZATION_STATUS_TIMEOUT) {
              clearInterval(intervalId);
              Redirect.redirectToError(paramPlusData, {
                reason: "payment_timeout",
              });
              return;
            }

            // still waiting for more significant status, so re-loop
            if (data.status === "3DS") {
              return;
            }

            if (data.status === "OK") {
              clearInterval(intervalId);
              this.runPostPaymentPipeline(paramPlusData);
            }
          },
        );
      }, 5000);
    },
    runPostPaymentPipeline(paramPlusData) {
      // Order Page UX: run Curiosity coregs and /extrasell prefetch in parallel.
      // The prefetch result is cached in sessionStorage so the confirmation page
      // can display the upsell instantly instead of waiting 3-4s after redirect.
      const coregsPromise = Curiosity.addCoregs(
        this.billingEmail,
        paramPlusData,
      ).catch((error) => {
        console.error(error);
      });

      const prefetchPromise = this.isOrderPage()
        ? this.prefetchExtrasell(paramPlusData)
        : Promise.resolve(null);

      Promise.allSettled([coregsPromise, prefetchPromise]).finally(() => {
        AlreadyOrderedProducts.updateAfterPayment();
        useDraftStore().updateHas1ClickOptOut(false);
        try {
          sessionStorage.setItem(
            `order_confirmed_${paramPlusData.orderId}`,
            "1",
          );
        } catch (_) {
          /* storage unavailable (privacy mode, etc.) */
        }
        const confirmationParamPlus = stampConfirmationOfferWindow({
          ...paramPlusData,
        });
        localStorage.setItem(
          constants.PARAM_PLUS,
          JSON.stringify(confirmationParamPlus),
        );
        Redirect.redirectToConfirmation(confirmationParamPlus);
      });
    },
    isOrderPage() {
      return Boolean(window?.globalConfig?.isOrderPage);
    },
    prefetchExtrasell(paramPlusData) {
      // Best-effort prefetch: capped at 2s, never blocks the redirect.
      const url = `${process.env.VUE_APP_ORION_API_BASE_URL}/extrasell`;
      const domainId = window?.globalConfig?.domainId;
      // Null-guard: skip prefetch when no order history exists yet (no payload to build).
      const hasHistory = Boolean(useOrderHistoryStore().lastOrderHistoryItem);

      if (!domainId || !hasHistory || !paramPlusData?.orderId) {
        return Promise.resolve(null);
      }

      const payload = this.buildPrefetchPayload(domainId);
      const orderId = paramPlusData.orderId;
      // Abort the axios request if the timeout wins the race.
      const controller =
        typeof AbortController !== "undefined" ? new AbortController() : null;

      const prefetch = fetchExtrasellWithStepAdvance(
        (extrasellUrl, extrasellPayload) =>
          axios.post(
            extrasellUrl,
            extrasellPayload,
            controller ? { signal: controller.signal } : {},
          ),
        url,
        payload,
      )
        .then((data) => {
          if (data?.stepType) {
            this.cacheExtrasell(orderId, data);
          }
          return data ?? null;
        })
        .catch((error) => {
          // Aborted requests are expected when the timeout wins; do not log them.
          if (axios.isCancel?.(error) || error?.name === "CanceledError") {
            return null;
          }
          console.error("[extrasell-prefetch] failed", error);
          return null;
        });

      const timeout = new Promise((resolve) => {
        setTimeout(() => {
          controller?.abort();
          resolve(null);
        }, EXTRASELL_PREFETCH_TIMEOUT_MS);
      });

      return Promise.race([prefetch, timeout]);
    },
    buildPrefetchPayload(domainId) {
      const store = useOrderHistoryStore();
      const historyFromStorage = selectExtrasellHistoryForPayload({
        orderHistory: store.orderHistory || [],
        currentPageOrderHistory: store.currentPageOrderHistory,
        paramPlusOrderId: Redirect.getParamPlusData()?.orderId || null,
        isPreviewSession: isVanguardOnlyExtrasellSession(),
      });

      return buildExtrasellPayload({
        historyFromStorage,
        domainId,
      });
    },
    cacheExtrasell(orderId, data) {
      if (!data) {
        return;
      }
      try {
        sessionStorage.setItem(
          `${EXTRASELL_CACHE_PREFIX}${orderId}`,
          JSON.stringify({
            data,
            expiresAt: Date.now() + EXTRASELL_CACHE_TTL_MS,
          }),
        );
      } catch (_) {
        /* storage unavailable */
      }
    },
    getOgoneStatus(authorizationId, orderId, byOrderId, callback) {
      if (byOrderId && orderId) {
        Palyance.getOgoneStatusByOrderId(orderId)
          .then((data) => {
            console.log(JSON.stringify(data), orderId);
            callback(data);
          })
          .catch((error) => {
            const message = `Error while getting ogone status : ${error.toString()}, orderId: ${orderId}`;
            console.error(message);
            callback(null, new Error(message));
          });
      } else if (authorizationId) {
        Palyance.getOgoneStatus(authorizationId)
          .then((data) => {
            console.log(JSON.stringify(data), orderId);
            callback(data);
          })
          .catch((error) => {
            const message = `Error while getting ogone status : ${error.toString()}, authorizationId: ${authorizationId}, orderId : ${orderId}`;
            console.error(message);
            callback(null, new Error(message));
          });
      } else if (orderId) {
        Palyance.getOgoneStatusByOrderId(orderId)
          .then((data) => {
            console.log(JSON.stringify(data), orderId);
            callback(data);
          })
          .catch((error) => {
            const message = `Error while getting ogone status : ${error.toString()}, orderId: ${orderId}`;
            console.error(message);
            callback(null, new Error(message));
          });
      }
    },
  },
};
</script>
